import { Clock, Target, Users } from "lucide-react";
import Input from "../ui/Input";

/**
 * Contract SLA + manager assign + agent engagement KPI fields with a simple journey visual.
 */
export default function SlaJourneyFields({
  values,
  onChange,
  institutionName = "This institution",
  readOnly = false,
}) {
  const contract = Number(values.contract_sla_days) || 0;
  const manager = Number(values.manager_assignment_days) || 0;
  const agent = Number(values.agent_engagement_days) || 0;
  const assignPct = contract > 0 ? Math.min(100, Math.round((manager / contract) * 100)) : 0;
  const engagePct = contract > 0 ? Math.min(100, Math.round((agent / contract) * 100)) : 0;

  const set = (key, raw) => {
    const num = raw === "" ? "" : Math.max(1, parseInt(String(raw), 10) || 0);
    onChange({ ...values, [key]: num });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-primary/[0.08] bg-primary/[0.02] px-4 py-3">
        <p className="text-xs leading-relaxed text-primary/55">
          Agreed recovery window for <span className="font-semibold text-primary">{institutionName}</span>.
          Manager and agent day budgets should fit inside the contract window so SimCollect KPIs match
          what you signed with the provider.
        </p>
      </div>

      {readOnly ? (
        <dl className="grid gap-3 sm:grid-cols-3">
          <SummaryCell label="Total SLA" value={`${values.contract_sla_days ?? "—"} days`} />
          <SummaryCell label="Manager assign KPI" value={`${values.manager_assignment_days ?? "—"} days`} />
          <SummaryCell label="Agent contact KPI" value={`${values.agent_engagement_days ?? "—"} days`} />
        </dl>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          <Input
            label="Total SLA (days)"
            type="number"
            min={1}
            value={values.contract_sla_days ?? ""}
            onChange={(e) => set("contract_sla_days", e.target.value)}
            placeholder="e.g. 20"
            required
          />
          <Input
            label="Manager assign within (days)"
            type="number"
            min={1}
            value={values.manager_assignment_days ?? ""}
            onChange={(e) => set("manager_assignment_days", e.target.value)}
            placeholder="e.g. 5"
            required
          />
          <Input
            label="Agent first contact (days)"
            type="number"
            min={1}
            value={values.agent_engagement_days ?? ""}
            onChange={(e) => set("agent_engagement_days", e.target.value)}
            placeholder="e.g. 2"
            required
          />
        </div>
      )}

      {contract > 0 && (manager > contract || agent > contract) ? (
        <p className="text-xs font-semibold text-amber-800">
          Manager or agent days exceed the contract window — adjust before launch.
        </p>
      ) : null}

      <div className="rounded-xl border border-primary/[0.08] bg-white p-4">
        <p className="text-[10px] font-bold uppercase tracking-[0.08em] text-primary/45">SLA journey</p>
        <div className="mt-4 space-y-3">
          <JourneyRow
            icon={Target}
            label="Portfolio uploaded"
            detail="Clock starts when cases enter SimCollect."
            tone="bg-primary/10 text-primary"
            widthPct={100}
          />
          <JourneyRow
            icon={Users}
            label="Manager assigns cases / portfolio"
            detail={`Within ${manager || "—"} day(s) · ${assignPct}% of ${contract || "—"}-day contract`}
            tone="bg-sky-100 text-sky-800"
            widthPct={assignPct || 8}
          />
          <JourneyRow
            icon={Clock}
            label="Agent first engagement"
            detail={`Within ${agent || "—"} day(s) after assign · ${engagePct}% of contract`}
            tone="bg-emerald-100 text-emerald-800"
            widthPct={engagePct || 8}
          />
        </div>
      </div>
    </div>
  );
}

function SummaryCell({ label, value }) {
  return (
    <div className="rounded-lg border border-primary/[0.08] bg-white px-3 py-2.5">
      <dt className="text-[10px] font-bold uppercase tracking-wide text-primary/40">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-primary">{value}</dd>
    </div>
  );
}

function JourneyRow({ icon: Icon, label, detail, tone, widthPct }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${tone}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-primary">{label}</p>
          <p className="text-[11px] text-primary/50">{detail}</p>
        </div>
      </div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-primary/[0.06]">
        <div
          className="h-full rounded-full bg-accent transition-all duration-300"
          style={{ width: `${Math.max(4, widthPct)}%` }}
        />
      </div>
    </div>
  );
}
